import {
    addSaveErrorAlert,
    addSaveSuccessAlert,
} from "../../actions/alert-actions"
import { entityCacheLimit } from "../../config/cache-limits"
import { Observable } from "rxjs/Observable"

export function mapRequestError(
    error,
    unauthenticatedAction,
    errorAction,
    ...actionArguments
) {
    return error.status === 401
        ? unauthenticatedAction()
        : errorAction(error, ...actionArguments)
}

const GENERIC_ERROR_MESSAGE = "An error has occurred, please try again."

// The reason the dashboard API gave for rejecting a request: it serialises 4xx errors as {"response": message}
// (see cherrypy_builder). Null when there isn't one, e.g. a network failure, or when another server answered.
export const apiErrorReason = (error) => {
    const reason = error && error.response && error.response.response
    return typeof reason === "string" && reason ? reason : null
}

// Turn an add-knowledge failure into a message the moderator can act on. Prefer a reason supplied by
// the API (a 4xx means the submitted link itself is the problem, e.g. it doesn't exist), then fall back
// to the message from a failed scrape, and only as a last resort to the generic "try again" - which is
// misleading for permanent failures the user cannot retry their way out of.
export function getKnowledgeErrorMessage(error) {
    if (!error) return null

    // The dashboard API serialises 4xx errors as {"response": message} (see cherrypy_builder); also
    // tolerate detail/message shapes from other origins. Prefer the API's own reason when present.
    const apiDetail =
        error.response &&
        (error.response.response ||
            error.response.detail ||
            error.response.message)
    if (apiDetail) return apiDetail

    // 4xx: the link/request is the problem and retrying won't help. 5xx/transport: genuinely transient.
    if (error.status >= 400 && error.status < 500) {
        return "We couldn't add that link. Please check the URL is correct and that the page exists."
    }
    if (error.status >= 500) {
        return "The site or service couldn't be reached right now. Please try again shortly."
    }

    // Non-ajax failures (e.g. a failed scrape surfaced via the status poll) are plain Errors with a
    // meaningful message; ajax errors are not (they read "ajax error N") and are handled by status above.
    if (!("status" in error) && error.message) return error.message

    return GENERIC_ERROR_MESSAGE
}

export function isEntityCached(isInvalidating, storedItem) {
    return (
        !isInvalidating &&
        storedItem &&
        storedItem.lastUpdatedAt &&
        Date.now() - storedItem.lastUpdatedAt < entityCacheLimit
    )
}

export function isListCached(
    isInvalidating,
    data,
    isPossibleNextPage,
    nextOffset,
    limit,
    lastUpdatedAt
) {
    return (
        !isInvalidating &&
        data &&
        Date.now() - lastUpdatedAt < entityCacheLimit &&
        (isPossibleNextPage === false || data.length >= nextOffset + limit)
    )
}

export function isListCountCached(isInvalidating, data, lastUpdatedAt) {
    return (
        !isInvalidating && data && Date.now() - lastUpdatedAt < entityCacheLimit
    )
}

export function getIn(state, accessor) {
    if (Array.isArray(accessor))
        return accessor.reduce(
            (previousPaging, accessorNext) =>
                previousPaging[accessorNext] || {},
            state
        )
    else return state[accessor]
}

export function getActionsToReplay(
    responseActions,
    saveActions,
    deleteActions,
    finishSaving,
    finishSavingError,
    entityType,
    entityName
) {
    let actionsToReplay = [...saveActions, ...deleteActions]
    responseActions = responseActions.filter((action) => action)

    if (responseActions.length > 0) {
        actionsToReplay = [...actionsToReplay, ...responseActions]
    }

    const hasErrored = responseActions.some(
        (action) => action && action.type.indexOf("ERROR") !== -1
    )

    if (!hasErrored) {
        actionsToReplay = [
            ...actionsToReplay,
            finishSaving(),
            addSaveSuccessAlert(entityName),
        ]
    } else {
        actionsToReplay = [
            ...actionsToReplay,
            finishSavingError(),
            addSaveErrorAlert(entityType, entityName),
        ]
    }

    return actionsToReplay
}

export function waitForAll(observables) {
    if (!observables || !observables.length) {
        return Observable.of([])
    } else {
        return Observable.forkJoin(observables)
    }
}

export const NOT_A_WEB_LINK_MESSAGE =
    "Enter a web link, such as https://www.example.com/article. Text and files saved on your computer can't be added."

// Links are scraped server-side, so only http(s) web links can be added. Returns the link to submit, with https://
// added when no scheme was typed (e.g. www.bbc.co.uk/news/...), or null for anything else - plain text, a local file
// path pasted from the browser such as file:///C:/Users/.../report.pdf, or a host with no domain - which would
// otherwise be sent to the API only to fail its validation with an unhelpful error.
export const toWebLink = (input) => {
    const trimmed = (input || "").trim()
    // Dots are deliberately not allowed in the scheme, so "example.com:8080/x" is treated as having none.
    const hasScheme = /^[a-z][a-z0-9+-]*:/i.test(trimmed)
    const link = hasScheme ? trimmed : `https://${trimmed}`
    try {
        const { protocol, hostname } = new URL(link)
        const isWebLink =
            ["http:", "https:"].includes(protocol) && hostname.includes(".")
        return isWebLink ? link : null
    } catch (error) {
        return null
    }
}
